import { ApiError } from '@commissionsight/sdk';

/**
 * The AI assistant is an app-only feature (not part of the public SDK surface),
 * so the web app calls the endpoint directly with the session token.
 */
export interface Citation {
  title: string;
  url: string;
}

/** A conversational-ingest proposal: what the assistant resolved from "ingest
 *  this as June WPS" — nothing happens until the user approves it. */
export interface ProposedIngest {
  attachmentId: string;
  fileName: string;
  carrierId: string;
  carrierName: string;
  periodYear: number;
  periodMonth: number;
  replace: boolean;
  periodLoaded: boolean;
  dryRun: { rowCount: number; mappedCount: number; mappedTotal: number } | null;
  statedTotal: { value: number; matchesMapped: boolean } | null;
  blockedByProductLine: boolean;
  productLineQuestion?: {
    prompt: string;
    suggested: ProductLineChoice | null;
    options: { value: ProductLineChoice; label: string; hint: string }[];
  };
}

export interface AssistantAnswer {
  question: string;
  /** Which skill answered: a data question, a recovery-report question, an investigation, a product how-to, or a "do it for me" ask. */
  intent?: 'book_query' | 'recovery_query' | 'investigate' | 'product_support' | 'action';
  answer: string;
  /** Present when the assistant proposes ingesting an attached statement. */
  proposedIngest?: ProposedIngest;
  /** Present for recovery answers — app path of the full report (proof-out / payment gaps). */
  reportPath?: string;
  /** Present for investigations — the tool steps taken, for the trace UI. */
  steps?: { tool: string; summary: string; ok: boolean }[];
  /** Present for book queries (the read-only SQL + result grid). */
  sql?: string;
  columns?: string[];
  rows?: Record<string, unknown>[];
  truncated?: boolean;
  /** Context-aware follow-up questions suggested from this answer + its data. */
  followUps?: string[];
  /** Present for product-support answers — links to the docs used. */
  citations?: Citation[];
  /** Present for "action" intent — false while the do-it-for-me skills are on the roadmap. */
  actionAvailable?: boolean;
  /** The persisted conversation this turn was appended to (send it back to continue). */
  conversationId?: string;
}

export interface ConversationHeader {
  id: string;
  title: string;
  turnCount: number;
  updatedAt: string;
}

export interface ConversationTurn {
  question: string;
  answer: string;
  intent: string | null;
  sql: string | null;
  reportPath: string | null;
  createdAt: string;
}

/** Persisted conversations (stored in the account's own data plane). */
export async function listConversations(
  baseUrl: string,
  token: string | null,
): Promise<{ data: ConversationHeader[] }> {
  const res = await fetch(`${baseUrl.replace(/\/$/, '')}/assistant/conversations`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new ApiError(res.status, `Failed to load conversations (${res.status})`);
  return res.json();
}

export async function getConversation(
  baseUrl: string,
  token: string | null,
  id: string,
): Promise<{ id: string; data: ConversationTurn[] }> {
  const res = await fetch(`${baseUrl.replace(/\/$/, '')}/assistant/conversations/${id}`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new ApiError(res.status, `Failed to load the conversation (${res.status})`);
  return res.json();
}

export async function removeConversation(
  baseUrl: string,
  token: string | null,
  id: string,
): Promise<void> {
  const res = await fetch(`${baseUrl.replace(/\/$/, '')}/assistant/conversations/${id}`, {
    method: 'DELETE',
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new ApiError(res.status, `Failed to delete the conversation (${res.status})`);
}

export interface IngestBrief {
  id: string;
  carrierId: string;
  carrierName: string | null;
  period: string;
  brief: string;
  createdAt: string;
}

/** Latest post-ingest briefs — the assistant's own summary of each processed statement. */
export async function listBriefs(
  baseUrl: string,
  token: string | null,
  limit = 3,
): Promise<{ data: IngestBrief[] }> {
  const res = await fetch(`${baseUrl.replace(/\/$/, '')}/assistant/briefs?limit=${limit}`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new ApiError(res.status, `Failed to load briefs (${res.status})`);
  return res.json();
}

export interface Watch {
  id: string;
  question: string;
  lastCount: number;
  lastRunAt: string | null;
  createdAt: string;
}

/** Standing questions the assistant re-runs after every ingest (emails on increase). */
export async function listWatches(
  baseUrl: string,
  token: string | null,
): Promise<{ data: Watch[] }> {
  const res = await fetch(`${baseUrl.replace(/\/$/, '')}/assistant/watches`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new ApiError(res.status, `Failed to load watches (${res.status})`);
  return res.json();
}

export async function createWatch(
  baseUrl: string,
  token: string | null,
  question: string,
): Promise<{ id: string }> {
  const res = await fetch(`${baseUrl.replace(/\/$/, '')}/assistant/watches`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify({ question }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { title?: string } | null;
    throw new ApiError(res.status, body?.title ?? `Failed to create watch (${res.status})`);
  }
  return res.json();
}

export async function deleteWatch(
  baseUrl: string,
  token: string | null,
  id: string,
): Promise<void> {
  const res = await fetch(`${baseUrl.replace(/\/$/, '')}/assistant/watches/${id}`, {
    method: 'DELETE',
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new ApiError(res.status, `Failed to delete watch (${res.status})`);
}

export interface MemoryNote {
  id: string;
  note: string;
  source: 'admin' | 'system' | 'customer';
  editable: boolean;
  createdAt: string;
}

/** What the assistant knows about this account (feeds every answer's context). */
export async function listMemory(
  baseUrl: string,
  token: string | null,
): Promise<{ data: MemoryNote[] }> {
  const res = await fetch(`${baseUrl.replace(/\/$/, '')}/assistant/memory`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new ApiError(res.status, `Failed to load memory (${res.status})`);
  return res.json();
}

export async function addMemory(
  baseUrl: string,
  token: string | null,
  note: string,
): Promise<MemoryNote> {
  const res = await fetch(`${baseUrl.replace(/\/$/, '')}/assistant/memory`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify({ note }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { title?: string } | null;
    throw new ApiError(res.status, body?.title ?? `Failed to add note (${res.status})`);
  }
  return res.json();
}

export async function removeMemory(
  baseUrl: string,
  token: string | null,
  id: string,
): Promise<void> {
  const res = await fetch(`${baseUrl.replace(/\/$/, '')}/assistant/memory/${id}`, {
    method: 'DELETE',
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new ApiError(res.status, `Failed to remove note (${res.status})`);
}

/** What the user picks when asked which product line a carrier's book is. */
export type ProductLineChoice = 'major_medical' | 'medicare' | 'medicare_supplement' | 'ancillary';

export interface PreflightQuestion {
  id: 'product_line' | 'rate_schedule';
  /** Blocking questions gate the Ingest button until answered. */
  blocking: boolean;
  prompt: string;
  suggested?: ProductLineChoice;
  options?: { value: ProductLineChoice; label: string; hint: string }[];
}

/** Pre-ingest guardrails: what we know about the product line, owed readiness,
 *  and the questions (if any) to answer BEFORE ingesting. */
export interface StatementPreflight {
  productLine: {
    configured: string | null;
    inferred: string;
    inferredReason: string | null;
    confirmed: string | null;
  };
  rateSchedule: { productLine: string; hasActive: boolean; cmsFallback: boolean } | null;
  questions: PreflightQuestion[];
}

export interface StatementPreview {
  /** Staged-attachment id — pass with follow-up questions so "ingest this" resolves. */
  attachmentId: string;
  fileName: string;
  fileType: 'csv' | 'xlsx';
  sampleRows: number;
  headers: string[];
  carrier: { id: string; name: string } | null;
  detection: {
    matchKind: 'signature' | 'scored' | 'none';
    signatureStatus: 'verified' | 'candidate' | null;
    ambiguous: boolean;
    inferredProductLine: string;
    ranked: { carrierId: string; carrierName: string; confidence: number }[];
  };
  shape: 'matches_active' | 'different_layout' | 'no_mapping' | 'unknown_carrier';
  dryRun: { rowCount: number; mappedCount: number; mappedTotal: number } | null;
  /** The statement's own printed total vs what the mapping carries. */
  statedTotal: {
    value: number;
    label: string;
    matchesMapped: boolean;
    variance: number;
  } | null;
  loadedPeriods: string[];
  preflight: StatementPreflight;
}

/** Preview a statement file WITHOUT ingesting it (nothing is stored). */
export async function previewStatement(
  baseUrl: string,
  token: string | null,
  file: File,
  carrierId?: string,
): Promise<StatementPreview> {
  const form = new FormData();
  form.set('file', file);
  if (carrierId) form.set('carrierId', carrierId);
  const res = await fetch(`${baseUrl.replace(/\/$/, '')}/assistant/statement-preview`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { title?: string } | null;
    throw new ApiError(res.status, body?.title ?? `Preview failed (${res.status})`);
  }
  return res.json();
}

export interface ProductLineConfirmResult {
  carrierId: string;
  choice: ProductLineChoice;
  productLine: string;
  forkedConfig: boolean;
  rateSchedule: { hasActive: boolean; cmsFallback: boolean };
}

/** Answer the preflight product-line question for a carrier (recorded once per
 *  carrier; a differing answer re-routes the owed engine from the next ingest). */
export async function confirmProductLine(
  baseUrl: string,
  token: string | null,
  carrierId: string,
  choice: ProductLineChoice,
): Promise<ProductLineConfirmResult> {
  const res = await fetch(`${baseUrl.replace(/\/$/, '')}/carriers/${carrierId}/product-line`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify({ choice }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { title?: string } | null;
    throw new ApiError(
      res.status,
      body?.title ?? `Could not save the product line (${res.status})`,
    );
  }
  return res.json();
}

/** Approve a proposed conversational ingest — lands the staged attachment
 *  through the normal ingest pipeline (202 + jobId). */
export async function ingestAttachment(
  baseUrl: string,
  token: string | null,
  args: {
    attachmentId: string;
    carrierId: string;
    periodYear: number;
    periodMonth: number;
    replace?: boolean;
  },
): Promise<{ jobId: string; fileId: string; isReplace: boolean }> {
  const res = await fetch(`${baseUrl.replace(/\/$/, '')}/assistant/ingest-attachment`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify(args),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { title?: string } | null;
    throw new ApiError(res.status, body?.title ?? `Ingest failed (${res.status})`);
  }
  return res.json();
}

/** Live-progress callbacks while an answer streams (both optional). */
export interface StreamHandlers {
  onStatus?: (label: string) => void;
  onStep?: (step: { tool: string; summary: string; ok: boolean }) => void;
}

/**
 * POST to an assistant endpoint requesting Server-Sent-Events progress
 * (status/step events, then `final` with the JSON payload, or `error`).
 * Falls back transparently to plain JSON when the server doesn't stream.
 */
async function postWithProgress(
  url: string,
  token: string | null,
  body: unknown,
  handlers: StreamHandlers = {},
): Promise<AssistantAnswer> {
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    accept: 'text/event-stream',
  };
  if (token) headers.authorization = `Bearer ${token}`;
  const res = await fetch(`${url}?stream=1`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const contentType = res.headers.get('content-type') ?? '';

  // Plain-JSON path: an error status, or a server that doesn't stream yet.
  if (!contentType.includes('text/event-stream')) {
    const text = await res.text();
    const parsed = text ? (JSON.parse(text) as unknown) : null;
    if (!res.ok) {
      const message =
        parsed && typeof parsed === 'object' && 'title' in parsed
          ? String((parsed as { title: unknown }).title)
          : res.statusText;
      throw new ApiError(res.status, message, parsed);
    }
    return parsed as AssistantAnswer;
  }

  // SSE path: parse `event:`/`data:` frames off the body stream.
  if (!res.body) throw new ApiError(res.status, 'Empty response stream');
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let eventName = 'message';
  let dataLines: string[] = [];
  let final: AssistantAnswer | null = null;
  let streamError: { message?: string } | null = null;

  const dispatch = () => {
    if (dataLines.length === 0) return;
    const raw = dataLines.join('\n');
    dataLines = [];
    let data: unknown;
    try {
      data = JSON.parse(raw);
    } catch {
      return;
    }
    if (eventName === 'status') {
      const label = (data as { label?: unknown }).label;
      if (typeof label === 'string') handlers.onStatus?.(label);
    } else if (eventName === 'step') {
      handlers.onStep?.(data as { tool: string; summary: string; ok: boolean });
    } else if (eventName === 'final') {
      final = data as AssistantAnswer;
    } else if (eventName === 'error') {
      streamError = data as { message?: string };
    }
    eventName = 'message';
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let nl = buffer.indexOf('\n');
    while (nl >= 0) {
      const line = buffer.slice(0, nl).replace(/\r$/, '');
      buffer = buffer.slice(nl + 1);
      if (line === '') {
        dispatch();
      } else if (line.startsWith('event:')) {
        eventName = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).replace(/^ /, ''));
      }
      nl = buffer.indexOf('\n');
    }
  }
  dispatch();
  if (streamError !== null) {
    throw new ApiError(500, (streamError as { message?: string }).message ?? 'Assistant failed');
  }
  if (final === null) throw new ApiError(500, 'The answer stream ended unexpectedly');
  return final;
}

/** Agentic multi-tool investigation of a question (explicitly user-triggered). */
export async function investigate(
  baseUrl: string,
  token: string | null,
  question: string,
  handlers: StreamHandlers = {},
  conversationId: string | null = null,
): Promise<AssistantAnswer> {
  return postWithProgress(
    `${baseUrl.replace(/\/$/, '')}/assistant/investigate`,
    token,
    { question, ...(conversationId ? { conversationId } : {}) },
    handlers,
  );
}

export async function askAssistant(
  baseUrl: string,
  token: string | null,
  question: string,
  history: { question: string; sql?: string }[] = [],
  handlers: StreamHandlers = {},
  conversationId: string | null = null,
  attachmentIds: string[] = [],
): Promise<AssistantAnswer> {
  return postWithProgress(
    `${baseUrl.replace(/\/$/, '')}/assistant/query`,
    token,
    {
      question,
      history,
      ...(conversationId ? { conversationId } : {}),
      ...(attachmentIds.length > 0 ? { attachmentIds } : {}),
    },
    handlers,
  );
}
